import { Button, Radio, Space, Tag, Typography } from "@arco-design/web-react";

import { MAX_FLOW_STEPS } from "../config";
import type { useApprovalSettingsRuntime } from "../runtime/useApprovalSettingsRuntime";
import { flowSteps, formatMemberName, minFlowSteps } from "../service/approvalSettingsService";
import type { ApprovalApproverType } from "../types";

type ApprovalStepListProps = {
  runtime: ReturnType<typeof useApprovalSettingsRuntime>;
  flow: number;
  onPick: (index: number) => void;
};

const approverTypeOptions: Array<{ label: string; value: ApprovalApproverType }> = [
  { label: "指定成员", value: "members" },
  { label: "所选部门的审批人", value: "dept_approver" },
];

/** 一条流程的逐级卡片：选审批人类型、加删、上下移、选人，错误写在对应的级下面。 */
export function ApprovalStepList({ runtime, flow, onPick }: ApprovalStepListProps) {
  const steps = runtime.draft ? flowSteps(runtime.draft, flow) : [];
  const errors = runtime.errors.flows[flow];
  const minSteps = minFlowSteps(flow);
  return (
    <div className="approval-step-list">
      {errors?.flowError && <Typography.Text type="error">{errors.flowError}</Typography.Text>}
      {steps.map((step, index) => (
        <div key={index} data-step-card className="approval-step-card">
          <div className="approval-step-header">
            <Typography.Text bold>第 {index + 1} 级</Typography.Text>
            <Space size="mini">
              <Button size="mini" disabled={index === 0} onClick={() => runtime.moveStep(flow, index, -1)}>上移</Button>
              <Button size="mini" disabled={index === steps.length - 1} onClick={() => runtime.moveStep(flow, index, 1)}>下移</Button>
              <Button size="mini" status="danger" disabled={steps.length <= minSteps} onClick={() => runtime.removeStep(flow, index)}>删除</Button>
            </Space>
          </div>
          <Radio.Group
            type="button"
            size="small"
            value={step.approverType}
            options={approverTypeOptions}
            onChange={(value: ApprovalApproverType) => runtime.setStepApproverType(flow, index, value)}
          />
          {step.approverType === "members" ? (
            <Space wrap>
              {step.memberIds.map((userId) => {
                const member = runtime.memberLookup.get(userId);
                return (
                  <Tag key={userId} closable color={member?.disabled ? "gray" : "arcoblue"}
                    onClose={() => runtime.setStepMembers(flow, index, step.memberIds.filter((id) => id !== userId))}>
                    {formatMemberName(member?.name ?? `用户 ${userId}`, member?.disabled ?? false)}
                  </Tag>
                );
              })}
              <Button size="mini" type="outline" onClick={() => onPick(index)}>选择成员</Button>
            </Space>
          ) : (
            <Typography.Text type="secondary">提交时按申请人选的部门，取组织和用户里该部门的审批人。</Typography.Text>
          )}
          {errors?.stepErrors[index] && <Typography.Text type="error">{errors.stepErrors[index]}</Typography.Text>}
        </div>
      ))}
      <Button type="dashed" long disabled={steps.length >= MAX_FLOW_STEPS} onClick={() => runtime.addStep(flow)}>添加一级</Button>
    </div>
  );
}
